'use client';

// P29.10 marketing FAQ accordion. shadcn Accordion (base-ui). Each open fires
// Plausible "FAQ Expanded" with the questionKey. JSON-LD FAQPage schema is
// emitted server-side in P29.17 from the same translation keys.

import { useTranslations } from 'next-intl';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { trackEvent } from '@/lib/analytics/plausible';

type Item = { key: string; question: string; answer: string };

type Props = { namespace?: string };

function trackExpand(questionKey: string) {
  trackEvent('FAQ Expanded', { questionKey });
}

export function FaqAccordion({ namespace = 'marketing.faq' }: Props = {}) {
  const t = useTranslations(namespace);
  const items = t.raw('items') as Item[];

  return (
    <section id="faq" data-testid="marketing-faq" className="bg-white py-20">
      <div className="mx-auto max-w-3xl px-4">
        <div className="text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            {t('sectionTitle')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
            {t('sectionSubtitle')}
          </p>
        </div>

        <Accordion className="mt-10">
          {items.map((item) => (
            <AccordionItem key={item.key} value={item.key} data-testid={`faq-item-${item.key}`}>
              <AccordionTrigger onClick={() => trackExpand(item.key)} className="text-slate-900">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                <p>{item.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
